import * as am5 from '@amcharts/amcharts5';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Responsive from '@amcharts/amcharts5/themes/Responsive';
import { ElementRef } from '@angular/core';
import { DateTime } from 'luxon';
import { RatioMap } from '../../models';
import { round } from 'lodash';

const colors = {
    twoYearPast: am5.color('#e10600'),
    oneYearPast: am5.color('#00d000'),
};

export function createGraph(
    ratios: RatioMap,
    container: ElementRef<any>,
    vehicleType: { currentYear: am5.Color },
) {
    const twoYearPast: {
        month: string;
        ratio: number;
        dateFormat: string;
        twoYearPast: string;
    }[] = ratios.twoYearPast.map((ratio) => {
        return {
            month: DateTime.fromJSDate(ratio.date).toFormat('LLL'),
            ratio: ratio.lt,
            dateFormat: DateTime.fromJSDate(ratio.date).toFormat('LLLL yyyy'),
            twoYearPast: `${round(ratio.lt, 2)}`,
        };
    });
    const oneYearPast: {
        month: string;
        ratio: number;
        oneYearFormat: string;
        oneYearPast: string;
    }[] = ratios.oneYearPast.map((ratio) => {
        return {
            month: DateTime.fromJSDate(ratio.date).toFormat('LLL'),
            ratio: round(ratio.lt, 2),
            oneYearFormat: DateTime.fromJSDate(ratio.date).toFormat(
                'LLLL yyyy',
            ),
            oneYearPast: `${round(ratio.lt, 2)}`,
        };
    });
    const currentYear: {
        month: string;
        ratio: number;
        currentYearFormat: string;
        currentYear: string;
    }[] = ratios.currentYear.map((ratio) => {
        return {
            month: DateTime.fromJSDate(ratio.date).toFormat('LLL'),
            ratio: ratio.lt,
            currentYearFormat: DateTime.fromJSDate(ratio.date).toFormat(
                'LLLL yyyy',
            ),
            currentYear: `${round(ratio.lt, 2)}`,
        };
    });

    const root = am5.Root.new(container.nativeElement);
    const responsive = am5themes_Responsive.new(root);
    responsive.addRule({
        relevant: function (width, height) {
            return width < 768;
        },
        applying: function () {
            xAxis.get('renderer').setAll({
                minGridDistance: 0,
            });
            yAxis.get('renderer').setAll({
                minGridDistance: 20,
            });
            xAxis.get('renderer').labels.template.setAll({
                fontSize: 10,
                paddingTop: 10,
            });
            yAxis.get('renderer').labels.template.setAll({
                fontSize: 10,
            });
            twoYearsPastSeries.strokes.template.setAll({
                strokeWidth: 6,
                opacity: 0.75,
            });
            oneYearPastSeries.strokes.template.setAll({
                strokeWidth: 6,
                opacity: 0.75,
            });
            currentYearSeries.strokes.template.setAll({
                strokeWidth: 6,
                opacity: 0.75,
            });
        },
        removing: function () {
            xAxis.get('renderer').setAll({
                minGridDistance: 20,
            });
            yAxis.get('renderer').setAll({
                minGridDistance: 40,
            });
            xAxis.get('renderer').labels.template.setAll({
                fontSize: 14,
                paddingTop: 20,
            });
            yAxis.get('renderer').labels.template.setAll({
                fontSize: 14,
            });
            twoYearsPastSeries.strokes.template.setAll({
                ...sharedLineStyles,
            });
            oneYearPastSeries.strokes.template.setAll({
                ...sharedLineStyles,
            });
            currentYearSeries.strokes.template.setAll({
                ...sharedLineStyles,
            });
        },
    });
    root.setThemes([am5themes_Animated.new(root), responsive]);

    let cursor = am5xy.XYCursor.new(root, {
        behavior: 'none',
    });

    cursor.lineX.setAll({
        stroke: am5.color('#ffffff'),
        strokeWidth: 2,
    });

    cursor.lineY.setAll({
        visible: false,
    });

    let chart = root.container.children.push(
        am5xy.XYChart.new(root, {
            cursor,
        }),
    );

    let xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
            categoryField: 'month',
            renderer: am5xy.AxisRendererX.new(root, {
                minGridDistance: 20,
                strokeOpacity: 1,
                stroke: am5.color('#ffffff'),
                strokeWidth: 2,
            }),
        }),
    );

    xAxis.set(
        'tooltip',
        am5.Tooltip.new(root, {
            themeTags: ['axis'],
            forceHidden: true,
        }),
    );
    
    let xRenderer = xAxis.get('renderer');

    xRenderer.labels.template.setAll({
        fill: am5.color('#ffffff'),
        textAlign: 'center',
        paddingTop: 20,
        fontSize: 14,
    });
    xRenderer.ticks.template.setAll({
        fill: am5.color('#ffffff'),
        visible: true,
    });

    xRenderer.grid.template.setAll({
        stroke: am5.color('#ffffff'),
        strokeWidth: 2,
    });

    let yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
            renderer: am5xy.AxisRendererY.new(root, {
                stroke: am5.color('#ffffff'),
                strokeWidth: 2,
                minGridDistance: 40,
                strokeOpacity: 1,
            }),
            panX: false,
            panY: false,
            numberFormat: '#',
            min: 0
        }),
    );
    let yRenderer = yAxis.get('renderer');

    yRenderer.labels.template.setAll({
        fill: am5.color('#ffffff'),
        fontWeight: 'bold',
        fontSize: 14,
        paddingRight: 10,
    });

    yRenderer.grid.template.setAll({
        stroke: am5.color('#ffffff'),
        strokeWidth: 2,
    });

    yRenderer.ticks.template.setAll({
        stroke: am5.color(0xff0000),
        visible: true,
    });

    const sharedLineStyles: Partial<am5.IGraphicsSettings> = {
        strokeWidth: 8,
        opacity: 0.75,
    };

    let tooltip = am5.Tooltip.new(root, {
        getFillFromSprite: false,
        getStrokeFromSprite: true,
        html: `
        <div class="grid grid-cols-2 gap-2 text-center">
            <section>
                <div  style="color:${colors.twoYearPast};"  class="label-small text-white">{dateFormat}</div>
                <div class="label-small-strong text-white">{twoYearPast}</div>
            </section>
            <section>
                
                <div  style="color:${colors.oneYearPast};"  class="label-small text-white">{oneYearFormat}</div>
                <div class="label-small-strong  text-white">{oneYearPast}</div>
            </section>
            <section>
                <div style="color:${vehicleType.currentYear};" class="label-small text-white">{currentYearFormat}</div>
                <div class="label-small-strong  text-white">{currentYear}</div>
            </section>
        </div>
        
        `,
    });

    tooltip.get('background').setAll({
        fill: am5.color('#000000'),
        fillOpacity: 0.8,
    });

    let twoYearsPastSeries = chart.series.push(
        am5xy.LineSeries.new(root, {
            name: 'Two Years Past Ratio Series',
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: 'ratio',
            categoryXField: 'month',
            stroke: colors.twoYearPast,
            tooltip,
        }),
    );
    let oneYearPastSeries = chart.series.push(
        am5xy.LineSeries.new(root, {
            name: 'One Year Past Ratio Series',
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: 'ratio',
            categoryXField: 'month',
            stroke: colors.oneYearPast,
        }),
    );
    let currentYearSeries = chart.series.push(
        am5xy.LineSeries.new(root, {
            name: 'Current Year Ratio Series',
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: 'ratio',
            categoryXField: 'month',
            stroke: vehicleType.currentYear,
        }),
    );

    twoYearsPastSeries.strokes.template.setAll({
        ...sharedLineStyles,
    });
    oneYearPastSeries.strokes.template.setAll({
        ...sharedLineStyles,
    });
    currentYearSeries.strokes.template.setAll({
        ...sharedLineStyles,
    });

    twoYearsPastSeries.bullets.push(function (root) {
        const sprite = am5.Circle.new(root, {
            radius: 4,
            fill: twoYearsPastSeries.get('stroke'),
        });

        sprite.states.create('hover', {
            scale: 2,
        });

        return am5.Bullet.new(root, {
            sprite,
        });
    });
    oneYearPastSeries.bullets.push(function (root) {
        const sprite = am5.Circle.new(root, {
            radius: 4,
            fill: oneYearPastSeries.get('stroke'),
        });

        return am5.Bullet.new(root, {
            sprite,
        });
    });
    currentYearSeries.bullets.push(function (root) {
        const sprite = am5.Circle.new(root, {
            radius: 4,
            fill: currentYearSeries.get('stroke'),
        });

        return am5.Bullet.new(root, {
            sprite,
        });
    });

    const twoYearPastData = twoYearPast.map((ratio, index) => {
        return currentYear[index]?.month?.length
            ? {
                  ...ratio,
                  currentYear: currentYear[index].currentYear,
                  oneYearPast: oneYearPast[index].oneYearPast,
                  oneYearFormat: oneYearPast[index].oneYearFormat,
                  currentYearFormat: currentYear[index].currentYearFormat,
              }
            : {
                  ...ratio,
                  oneYearPast: oneYearPast[index].oneYearPast,
                  oneYearFormat: oneYearPast[index].oneYearFormat,
              };
    });

    twoYearsPastSeries.data.setAll(twoYearPastData);
    oneYearPastSeries.data.setAll(oneYearPast);
    currentYearSeries.data.setAll(currentYear);
    twoYearsPastSeries.appear(1000).then(() => {});
    oneYearPastSeries.appear(1000).then(() => {});
    currentYearSeries.appear(1000).then(() => {});
    xAxis.data.setAll(twoYearPast);
    chart.appear(1).then(() => {});

    cursor.setAll({
        //snapToSeries: [series, series2],
        xAxis: xAxis,
    });

    return chart;
}
